/**
 * Client build-freshness policy (Issue #320).
 *
 * A britbank deploy is fast enough that players do **not** fully disconnect: the
 * WebSocket blips and the client auto-reconnects (250ms-5s backoff) when the server
 * is back. Because the page never reloads, players keep running the **old client
 * bundle** after a patch. To fix that, the client bakes its own build id
 * (`VITE_BUILD_ID`) and the server reports its build id in every `Welcome`; on each
 * reconnect the client compares the two and refreshes onto the new bundle when they
 * differ.
 *
 * This module is the **pure decision layer** - no DOM, no `location`, no timers of
 * its own - so the whole compare/prompt/force/jitter policy is unit-testable
 * headlessly. `main.ts` supplies the browser effects (show the prompt overlay,
 * navigate, `setTimeout`, `Math.random`) as injected dependencies. The two hooks
 * mirror the {@link import('./net/gameClient').GameClient} callbacks:
 * {@link UpdateController.onServerBuild} (every Welcome) and
 * {@link UpdateController.onVersionSkew} (a snapshot the stale client cannot read).
 */

/**
 * The largest random delay (ms) added before a **forced** reload. On a redeploy every
 * client's socket drops at once, so a synchronized force-reload would have the whole
 * room re-download the new bundle simultaneously (a thundering herd on a single VM).
 * Spreading each client's reload across `[0, this]` blunts that spike. The prompt path
 * needs no jitter - human reaction time already staggers those reloads.
 */
export const UPDATE_RELOAD_MAX_JITTER_MS = 3000;

/**
 * Whether the server is running a **different** build than this client. True only when
 * **both** ids are known (non-empty) and they differ: an empty local id (dev, or a
 * deploy that never baked `VITE_BUILD_ID`) or an absent server id (dev/tests, or a
 * deploy that never threaded `BUILD_ID`) makes the compare a deliberate no-op rather
 * than a false alarm - the "both deploy paths must be wired or it silently no-ops"
 * contract from the issue.
 */
export function buildMismatch(localBuildId: string, serverBuildId: string | undefined): boolean {
  return (
    localBuildId.length > 0 &&
    typeof serverBuildId === 'string' &&
    serverBuildId.length > 0 &&
    serverBuildId !== localBuildId
  );
}

/** Dependencies injected by `main.ts`; every browser effect is a seam so tests pin it. */
export interface UpdateControllerDeps {
  /** This client bundle's baked build id (`VITE_BUILD_ID`), or `''` when unknown. */
  localBuildId: string;
  /**
   * Show the non-blocking "a new version is available" prompt. Its reload control must
   * call the supplied `applyReload`; the player may also dismiss it and keep playing.
   */
  showPrompt: (applyReload: () => void) => void;
  /** Perform the actual page refresh onto the new bundle for the given server build id. */
  reload: (serverBuildId: string | undefined) => void;
  /** Schedule `fn` after `ms`; defaults to `setTimeout`. Injected so tests run it eagerly. */
  schedule?: (fn: () => void, ms: number) => void;
  /** Jitter source in `[0, 1)`; defaults to `Math.random`. Injected so tests pin the delay. */
  rng?: () => number;
  /**
   * Cross-page-load one-shot guard for a **forced** reload (sessionStorage-backed in
   * `main.ts`). The in-memory `reloadScheduled` flag resets on every load, so without this
   * a bundle that genuinely can't self-update (a CDN/proxy ignoring `no-cache` on the shell
   * and the `?v=` cache-bust) would re-schedule a reload on every reconnect - a whole-room
   * infinite reload loop. `take()` records that a forced reload fired (survives the reload);
   * `taken()` blocks a second one until `clear()` runs. It is cleared only once a **matching**
   * build is observed (`onServerBuild` with no mismatch) - never on a mere successful Welcome,
   * because a forced reload onto a *still-stale* bundle also produces a Welcome and clearing
   * there would defeat the guard. Defaults to a no-op (always allows) for tests.
   */
  forcedReloadGuard?: {
    taken(): boolean;
    take(): void;
    clear(): void;
  };
}

/** The two reconnect-time hooks `main.ts` wires to the {@link import('./net/gameClient').GameClient}. */
export interface UpdateController {
  /**
   * Called on **every** `Welcome` (so once per reconnect) with the server's build id
   * and force flag. A mismatch shows the reload prompt, or - when `forceReload` is set
   * - schedules a jittered forced reload. A match, or an unknown id on either side, is
   * a no-op. Idempotent per page load: at most one prompt / one scheduled reload.
   */
  onServerBuild(serverBuildId: string | undefined, forceReload: boolean): void;
  /**
   * Called when the client **cannot deserialize** a server snapshot - the load-bearing
   * case being a `SCHEMA_VERSION` skew after a breaking deploy. The stale client can't
   * proceed, so this forces a jittered reload regardless of the prompt/force policy
   * (still at most once per page load, and gated by the shared {@link
   * UpdateControllerDeps.forcedReloadGuard} so a genuinely un-updatable bundle can't
   * reload-loop across page loads).
   */
  onVersionSkew(): void;
}

/**
 * Build an {@link UpdateController} over the injected effects. Holds only two booleans
 * of state (one prompt, one scheduled reload) plus the last server build id seen, so a
 * reconnect storm can never stack prompts or schedule a second reload within one load.
 */
export function createUpdateController(deps: UpdateControllerDeps): UpdateController {
  const { localBuildId, showPrompt, reload } = deps;
  const schedule = deps.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
  const rng = deps.rng ?? Math.random;
  const forcedReloadGuard = deps.forcedReloadGuard ?? {
    taken: () => false,
    take: () => {},
    clear: () => {},
  };

  let promptShown = false;
  let reloadScheduled = false;
  let lastServerBuildId: string | undefined;

  const scheduleForcedReload = (serverBuildId: string | undefined): void => {
    if (reloadScheduled) return;
    // A forced reload already fired this tab session and we're still not on a fresh
    // build: refuse to schedule another so a bundle that can't self-update can't loop.
    if (forcedReloadGuard.taken()) return;
    reloadScheduled = true;
    forcedReloadGuard.take();
    // Uniform jitter across [0, MAX] (inclusive) so a whole-room reload doesn't
    // synchronize into one bundle-download spike when the server returns.
    const delay = Math.floor(rng() * (UPDATE_RELOAD_MAX_JITTER_MS + 1));
    schedule(() => reload(serverBuildId), delay);
  };

  return {
    onServerBuild(serverBuildId, forceReload) {
      lastServerBuildId = serverBuildId;
      if (!buildMismatch(localBuildId, serverBuildId)) {
        // On a build we can serve (ids match, or the compare is disabled in dev): a prior
        // forced reload succeeded, so release the one-shot guard - a *later* independent
        // deploy in this same tab may force again.
        forcedReloadGuard.clear();
        return;
      }
      if (forceReload) {
        scheduleForcedReload(serverBuildId);
        return;
      }
      // Prompt by default (protects a mid-duel / mid-storm / mid-chat player from being
      // yanked). Once shown, a reconnect's repeat Welcome must not re-open it.
      if (promptShown || reloadScheduled) return;
      promptShown = true;
      showPrompt(() => reload(serverBuildId));
    },
    onVersionSkew() {
      // The world is unreadable, so a prompt the player might ignore isn't enough - the
      // session is already broken. Force the refresh (jittered, once) even if the
      // operator didn't set forceReload, using whatever server build id we last saw.
      scheduleForcedReload(lastServerBuildId);
    },
  };
}
